/**
 * Accessibility utilities and helpers
 */

/**
 * Generate unique IDs for form elements and ARIA relationships
 */
export const generateId = (prefix = 'element') => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Announce content to screen readers
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Focus management utilities
 */
export const focusManagement = {
  /**
   * Trap focus within a container
   */
  trapFocus: (container) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };
    
    container.addEventListener('keydown', handleTabKey);
    
    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  },

  /**
   * Focus first focusable element in container
   */
  focusFirst: (container) => {
    const focusableElement = container.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElement) {
      focusableElement.focus();
    }
  },

  /**
   * Return focus to previously focused element
   */
  returnFocus: (element) => {
    if (element && typeof element.focus === 'function') {
      element.focus();
    }
  }
};

/**
 * Keyboard navigation helpers
 */
export const keyboardNavigation = {
  /**
   * Handle arrow key navigation for lists/grids
   */
  handleArrowKeys: (e, items, currentIndex, orientation = 'horizontal') => {
    let newIndex = currentIndex;
    
    switch (e.key) {
      case 'ArrowRight':
        if (orientation === 'horizontal') {
          newIndex = (currentIndex + 1) % items.length;
        }
        break;
      case 'ArrowLeft':
        if (orientation === 'horizontal') {
          newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
        }
        break;
      case 'ArrowDown':
        if (orientation === 'vertical') {
          newIndex = (currentIndex + 1) % items.length;
        }
        break;
      case 'ArrowUp':
        if (orientation === 'vertical') {
          newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
        }
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = items.length - 1;
        break;
      default:
        return currentIndex;
    }
    
    e.preventDefault();
    items[newIndex]?.focus();
    return newIndex;
  },

  /**
   * Handle escape key to close modals/dropdowns
   */
  handleEscape: (e, callback) => {
    if (e.key === 'Escape') {
      callback();
    }
  }
};

/**
 * Color contrast utilities
 */
export const colorContrast = {
  /**
   * Check if color combination meets WCAG contrast requirements
   */
  meetsContrastRequirement: (foreground, background, level = 'AA') => {
    // This is a simplified check - in production, use a proper contrast calculation library
    
    
    // Placeholder implementation - replace with actual contrast calculation
    return true; // Assume our design meets requirements
  },

  /**
   * Get accessible color combinations
   */
  getAccessibleColors: () => ({
    primary: {
      background: '#2563eb',
      text: '#ffffff',
      contrast: 7.2
    },
    secondary: {
      background: '#f8fafc',
      text: '#1e293b',
      contrast: 12.6
    }
  })
};

/**
 * Screen reader utilities
 */
export const screenReader = {
  /**
   * Hide content from screen readers
   */
  hide: (element) => {
    element.setAttribute('aria-hidden', 'true');
  },

  /**
   * Show content to screen readers
   */
  show: (element) => {
    element.removeAttribute('aria-hidden');
  },

  /**
   * Create screen reader only text
   */
  createSROnlyText: (text) => {
    const span = document.createElement('span');
    span.className = 'sr-only';
    span.textContent = text;
    return span;
  }
};

/**
 * Motion and animation preferences
 */
export const motionPreferences = {
  /**
   * Check if user prefers reduced motion
   */
  prefersReducedMotion: () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  /**
   * Apply motion settings based on user preference
   */
  applyMotionPreference: (element, animations) => {
    if (motionPreferences.prefersReducedMotion()) {
      // Disable or reduce animations
      element.style.animation = 'none';
      element.style.transition = 'none';
    } else {
      // Apply full animations
      if (animations.animation) {
        element.style.animation = animations.animation;
      }
      if (animations.transition) {
        element.style.transition = animations.transition;
      }
    }
  }
};

/**
 * Form accessibility helpers
 */
export const formAccessibility = {
  /**
   * Associate label with form control
   */
  associateLabel: (labelElement, inputElement) => {
    const id = generateId('input');
    inputElement.id = id;
    labelElement.setAttribute('for', id);
  },

  /**
   * Add error message to form control
   */
  addErrorMessage: (inputElement, errorMessage) => {
    const errorId = generateId('error');
    const errorElement = document.createElement('div');
    errorElement.id = errorId;
    errorElement.className = 'error-message';
    errorElement.textContent = errorMessage;
    errorElement.setAttribute('role', 'alert');
    
    inputElement.setAttribute('aria-describedby', errorId);
    inputElement.setAttribute('aria-invalid', 'true');
    inputElement.parentNode.appendChild(errorElement);
    
    return errorElement;
  },

  /**
   * Remove error message from form control
   */
  removeErrorMessage: (inputElement) => {
    const errorId = inputElement.getAttribute('aria-describedby');
    if (errorId) {
      const errorElement = document.getElementById(errorId);
      if (errorElement) {
        errorElement.remove();
      }
      inputElement.removeAttribute('aria-describedby');
      inputElement.removeAttribute('aria-invalid');
    }
  }
};

/**
 * Landmark and heading utilities
 */
export const landmarks = {
  /**
   * Validate heading hierarchy
   */
  validateHeadingHierarchy: () => {
    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const issues = [];
    let previousLevel = 0;
    
    headings.forEach((heading, index) => {
      const currentLevel = parseInt(heading.tagName.charAt(1));
      
      if (index === 0 && currentLevel !== 1) {
        issues.push('First heading should be h1');
      }
      
      if (currentLevel > previousLevel + 1) {
        issues.push(`Heading level jumps from h${previousLevel} to h${currentLevel}`);
      }
      
      previousLevel = currentLevel;
    });
    
    return issues;
  },

  /**
   * Ensure proper landmark structure
   */
  validateLandmarks: () => {
    const requiredLandmarks = ['main', 'navigation', 'contentinfo'];
    const issues = [];
    
    requiredLandmarks.forEach(landmark => {
      const element = document.querySelector(`[role="${landmark}"], ${landmark}`);
      if (!element) {
        issues.push(`Missing ${landmark} landmark`);
      }
    });
    
    return issues;
  }
};

/**
 * Create skip link for keyboard navigation
 */
export const createSkipLink = (targetId = 'main', text = 'Skip to main content') => {
  const skipLink = document.createElement('a');
  skipLink.href = `#${targetId}`;
  skipLink.textContent = text;
  skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md focus:shadow-lg';
  skipLink.setAttribute('tabindex', '0');
  
  // Insert at the beginning of the body
  document.body.insertBefore(skipLink, document.body.firstChild);
  
  return skipLink;
};

export default {
  generateId,
  announceToScreenReader,
  focusManagement,
  keyboardNavigation,
  colorContrast,
  screenReader,
  motionPreferences,
  formAccessibility,
  landmarks,
  createSkipLink
};